import { ActiveUser } from '../models';

/**
 * Get initials for an active user based on display name or username
 * Priority:
 * 1. If displayName has first/last name format -> use first/last initials
 * 2. If username has multiple words -> first letter of first two words
 * 3. If username has 2+ capitals (not all caps) -> first 2 capitals
 * 4. Otherwise -> first 2 letters of username
 */
export function getActiveUserInitials(user: ActiveUser): string {
  // If displayName exists and has first/last name format
  if (user.displayName) {
    const nameParts = user.displayName.split(' ').filter((part) => part.length > 0);

    // Check if it's an "Unknown User" format
    const isUnknownUser = user.displayName.toLowerCase().includes('unknown') && user.displayName.toLowerCase().includes('user');

    // If we have a proper first/last name (not Unknown User format)
    if (nameParts.length >= 2 && !isUnknownUser) {
      const first = nameParts[0].charAt(0).toUpperCase();
      const last = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
      return `${first}${last}`;
    }

    // If it's Unknown User or single name, fall through to username logic
  }

  // Use username with smart capital letter extraction
  return extractInitialsFromUsername(user.username);
}

/**
 * Extract initials from username with smart logic
 * Priority:
 * 1. Multi-word username -> first letter of first two words
 * 2. CamelCase (2+ capitals, not all caps) -> first 2 capitals
 * 3. Otherwise -> first 2 letters
 */
function extractInitialsFromUsername(username: string): string {
  if (!username || username.length === 0) {
    return 'UU';
  }

  // Check if username contains multiple words (space-separated)
  const words = username.split(' ').filter((word) => word.length > 0);
  if (words.length >= 2) {
    // Use first letter of first two words
    const first = words[0].charAt(0).toUpperCase();
    const second = words[1].charAt(0).toUpperCase();
    return `${first}${second}`;
  }

  // Extract capital letters from username
  const capitals = username.split('').filter((char) => char >= 'A' && char <= 'Z');

  // Check if all letters are capitals
  const allCaps = username.toUpperCase() === username;

  // If we have 2+ capitals and not all caps, use first 2 capitals
  if (capitals.length >= 2 && !allCaps) {
    return capitals.slice(0, 2).join('');
  }

  // Otherwise use first 2 letters of username
  return username.substring(0, 2).toUpperCase();
}

/**
 * Format active user timestamp as relative time
 * Returns: "just now", "5 minutes ago", "2 hours ago", or "recently" if no timestamp
 */
export function formatActiveUserTimestampRelative(user: ActiveUser): string {
  if (!user.accessTimestamp) {
    return 'recently';
  }

  const now = new Date();
  const timestamp = new Date(user.accessTimestamp);
  const diffMs = now.getTime() - timestamp.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) {
    return 'just now';
  } else if (diffMins < 60) {
    return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  } else {
    const diffHours = Math.floor(diffMins / 60);
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
}

/**
 * Format active user timestamp as full date/time
 * Returns: "12/24/25, 2:30 PM PST" or "recently" if no timestamp
 */
export function formatActiveUserTimestampFull(user: ActiveUser): string {
  if (!user.accessTimestamp) {
    return 'recently';
  }

  const timestamp = new Date(user.accessTimestamp);
  const options: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  };

  return timestamp.toLocaleString('en-US', options);
}

/**
 * Get application label for display (e.g., "Byte", "Clear", "Unknown")
 *
 * Source Application Mapping:
 * - 'byte': Direct Byte application events
 * - 'eventfunc': Azure Function that processes Byte application events
 *                (EventFunc represents Byte users accessing files through background processes)
 * - 'eps.loan': Legacy EPS system (mapped to Byte for consistency with styling)
 * - 'clear' / 'clear2': Clear 2.0 web application
 *
 * Note: EPS.Loan users are filtered out by isOtherUser() and should never appear in the UI,
 * but are mapped to 'Byte' here for consistency with getActiveUserApplicationType() styling.
 */
export function getActiveUserApplicationLabel(user: ActiveUser): string {
  const source = user.sourceApplication?.toLowerCase();
  if (source === 'byte' || source === 'eventfunc' || source === 'eps.loan') {
    return 'Byte';
  }
  if (source === 'clear' || source === 'clear2') {
    return 'Clear';
  }
  return 'Unknown';
}

/**
 * Get application type for styling purposes (CSS classes)
 * Used to determine which color/style to apply to user avatars
 *
 * Type Mapping:
 * - 'byte': Byte application, EventFunc (Azure Function for Byte), and EPS.Loan (legacy - styled like Byte)
 * - 'clear': Clear 2.0 web application
 * - 'unknown': Unrecognized source applications
 *
 * @returns 'byte' | 'clear' | 'unknown'
 */
export function getActiveUserApplicationType(user: ActiveUser): 'byte' | 'clear' | 'unknown' {
  const source = user.sourceApplication?.toLowerCase();
  // Byte ecosystem: Byte app, EventFunc (processes Byte events), and EPS.Loan (legacy system)
  if (source === 'byte' || source === 'eventfunc' || source === 'eps.loan') {
    return 'byte';
  }
  // Clear ecosystem: Clear 2.0 web application
  if (source === 'clear' || source === 'clear2') {
    return 'clear';
  }
  return 'unknown';
}

/**
 * Get formatted full name for display
 * Handles "Unknown User" formatting with application source
 * Returns: "Unknown Byte User", "Unknown Clear User", "Unknown User", or the displayName/username
 */
export function getActiveUserFullName(user: ActiveUser): string {
  const displayName = user.displayName || user.username;

  // Handle "Unknown" users - format as "Unknown {Source} User" or just "Unknown User"
  if (displayName.toLowerCase().includes('unknown') && displayName.toLowerCase().includes('user')) {
    const source = getActiveUserApplicationLabel(user);
    return source === 'Unknown' ? 'Unknown User' : `Unknown ${source} User`;
  }

  return displayName;
}

/**
 * Deduplicate active users list by keeping oldest instance per username+source combination
 *
 * When a user has multiple tabs/sessions open in the same application, we only show the
 * oldest (first opened) instance. This prevents duplicate entries in the active users list
 * while preserving cross-application presence (e.g., same user in both Clear and Byte).
 *
 * @param users - Array of active users that may contain duplicates
 * @returns Deduplicated array with oldest instance per username+source combination
 *
 * @example
 * // User "jsmith" has 2 Clear tabs open with timestamps 10:00 and 10:05
 * // Result: Only the 10:00 instance is kept (oldest)
 *
 * @example
 * // User "jsmith" has Clear (10:00) and Byte (10:03) open
 * // Result: Both instances kept (different sources = cross-app presence)
 */
export function deduplicateActiveUsers(users: ActiveUser[]): ActiveUser[] {
  // Group users by username + sourceApplication key
  const userMap = new Map<string, ActiveUser>();

  for (const user of users) {
    // Create composite key: username + source (case-insensitive)
    const source = user.sourceApplication?.toLowerCase() || 'unknown';
    const key = `${user.username.toLowerCase()}|${source}`;

    const existing = userMap.get(key);

    if (!existing) {
      // First instance of this user+source combination
      userMap.set(key, user);
    } else {
      // Duplicate found - keep the oldest instance (earliest timestamp)
      const existingTime = existing.accessTimestamp ? new Date(existing.accessTimestamp).getTime() : 0;
      const currentTime = user.accessTimestamp ? new Date(user.accessTimestamp).getTime() : 0;

      // Keep whichever has the earlier timestamp (or existing if timestamps are equal/missing)
      if (currentTime > 0 && currentTime < existingTime) {
        userMap.set(key, user);
      }
    }
  }

  return Array.from(userMap.values());
}

/**
 * Determine if a user should be included in the "other users" list
 *
 * Filtering Logic:
 * 1. EPS.Loan users are ALWAYS filtered out (legacy system - not relevant for active user tracking)
 * 2. EPSLoanApiUser is filtered out (system/service account - not a real user)
 * 3. Users with different usernames are included (different person in the file)
 * 4. Users with same username are only included if from Byte/EventFunc (cross-application presence)
 *
 * Why EventFunc is treated like Byte:
 * EventFunc is an Azure Function that processes Byte application events. When a user performs
 * actions in Byte that trigger file modifications, EventFunc executes on their behalf. Both
 * 'byte' and 'eventfunc' sources represent the same Byte application ecosystem.
 *
 * Cross-Application Presence Detection:
 * This logic allows detecting when the same person is simultaneously active in both Clear
 * and Byte (including EventFunc operations). For example, if user "jsmith" is viewing a loan
 * in Clear while also having automated Byte processes running, both presences are shown.
 *
 * @param user - The active user to evaluate
 * @param currentUsername - The current logged-in user's username
 * @returns true if the user should be shown in the "other users" list
 */
export function isOtherUser(user: ActiveUser, currentUsername: string): boolean {
  const source = user.sourceApplication?.toLowerCase();

  // If current username is not yet available (session not fully initialized),
  // don't show anyone as "other" - prevents self-notifications during race conditions
  if (!currentUsername) {
    return false;
  }

  // Filter out EPS.Loan users - legacy system not relevant for active user tracking
  if (source === 'eps.loan') {
    return false;
  }

  // Filter out EPSLoanApiUser - system/service account (case-insensitive)
  if (user.username.toLowerCase() === 'epsloanapiuser') {
    return false;
  }

  // Different username - always include (different person accessing the file)
  // Use case-insensitive comparison to match deduplication logic
  if (user.username.toLowerCase() !== currentUsername.toLowerCase()) {
    return true;
  }

  // Same username - only include if from Byte or EventFunc to show cross-application presence
  // This detects when the current user is active in both Clear and Byte simultaneously
  return source === 'byte' || source === 'eventfunc';
}
